import { Box, Container, Typography, IconButton } from '@mui/material'
import {
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import type { ReactNode } from 'react'
import {
  SITE_FOOTER_NAV_LINKS,
  SITE_FOOTER_SOCIAL_LINKS,
  siteFooterCopyright,
  type SiteFooterNavLink,
  type SiteFooterSocialLink,
} from './siteFooterLinks'

export type SiteFooterVariant = 'brand' | 'compact'

export interface SiteFooterProps {
  /** brand = blue marketing chrome; compact = light app-shell chrome */
  variant?: SiteFooterVariant
  /** Override nav links (e.g. app-relative legal URLs) */
  links?: SiteFooterNavLink[]
  socialLinks?: SiteFooterSocialLink[]
  /** Optional brand surface overrides (e.g. role-assumed tint) */
  brandBg?: string
  brandBorder?: string
  year?: number
}

const socialIcons: Record<SiteFooterSocialLink['network'], ReactNode> = {
  github: <GitHubIcon />,
  linkedin: <LinkedInIcon />,
  twitter: <TwitterIcon />,
  email: <EmailIcon />,
}

/**
 * Canonical VeilStream site footer — copyright, legal/pricing links, social icons.
 */
export function SiteFooter({
  variant = 'brand',
  links = SITE_FOOTER_NAV_LINKS,
  socialLinks = SITE_FOOTER_SOCIAL_LINKS,
  brandBg,
  brandBorder,
  year = new Date().getFullYear(),
}: SiteFooterProps) {
  const compact = variant === 'compact'
  const muted = compact ? 'text.secondary' : 'rgba(255, 255, 255, 0.8)'
  const hover = compact ? 'text.primary' : 'white'

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: compact ? 'background.paper' : brandBg ?? 'primary.main',
        borderTop: 1,
        borderColor: compact ? 'divider' : brandBorder ?? 'primary.dark',
        mt: 'auto',
        color: compact ? 'text.secondary' : 'white',
      }}
    >
      <Container maxWidth="lg" sx={{ py: compact ? 1.25 : 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color={muted}>
              {siteFooterCopyright(year)}
            </Typography>
            {links.map((link) => (
              <Typography
                key={link.label}
                variant="body2"
                component="a"
                href={link.href}
                target={link.openInNewTab ? '_blank' : undefined}
                rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                sx={{
                  color: muted,
                  textDecoration: 'none',
                  '&:hover': {
                    color: hover,
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {socialLinks.map((social) => (
              <IconButton
                key={social.label}
                href={social.href}
                target={social.network === 'email' ? undefined : '_blank'}
                rel={social.network === 'email' ? undefined : 'noopener noreferrer'}
                aria-label={social.label}
                size="small"
                sx={{
                  color: muted,
                  '&:hover': { color: hover },
                }}
              >
                {socialIcons[social.network]}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default SiteFooter
